import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Paper,
  Grid,
  Chip,
  Autocomplete,
  FormGroup,
  Checkbox,
  FormLabel,
  RadioGroup,
  Radio,
  Divider,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { adsAPI, applicationsAPI } from '../services/api';

// Типы рекламных блоков
const AD_TYPES = [
  { value: 'ads_banners', label: 'ads_banners', description: 'Баннерная реклама в интерфейсе' },
  { value: 'ads_banners_tv', label: 'ads_banners_tv', description: 'Баннерная реклама (ТВ устройства) — слово banners_tv в ads_strategy_banner_tv' },
  { value: 'ads_interstitial', label: 'ads_interstitial', description: 'Полноэкранная реклама между экранами' },
  { value: 'ads_interstitial_tv', label: 'ads_interstitial_tv', description: 'Межстраничная реклама для ТВ устройств' },
  { value: 'ads_interstitial_native_tv', label: 'ads_interstitial_native_tv', description: 'Нативная реклама для ТВ' },
  { value: 'ads_interstitial_more', label: 'ads_interstitial_more', description: 'Дополнительные межстраничные блоки' },
  { value: 'ads_feed', label: 'ads_feed', description: 'Реклама внутри списка каналов' },
  { value: 'ads_native', label: 'ads_native', description: 'Нативная реклама (мобильные устройства)' },
  { value: 'ads_native_tv', label: 'ads_native_tv', description: 'Нативная реклама (ТВ устройства)' },
  { value: 'ads_start', label: 'ads_start', description: 'Реклама при запуске приложения' },
  { value: 'ads_instream', label: 'ads_instream', description: 'Реклама во время просмотра' },
  { value: 'ads_instream_tv', label: 'ads_instream_tv', description: 'Внутрипотоковая реклама для ТВ' }
];

// SDK типы
const SDK_TYPES = [
  { value: 'yandex', label: 'Yandex', description: 'Yandex Mobile Ads' },
  { value: 'mytarget', label: 'MyTarget', description: 'Mail.ru MyTarget' },
  { value: 'google', label: 'Google AdMob', description: 'Google AdMob' },
  { value: 'ima', label: 'Google IMA', description: 'Google Interactive Media Ads (для instream рекламы)' },
  { value: 'facebook', label: 'Facebook', description: 'Facebook Audience Network' },
  { value: 'unity', label: 'Unity Ads', description: 'Unity Ads Network' },
  { value: 'applovin', label: 'AppLovin', description: 'AppLovin MAX' },
  { value: 'ironsource', label: 'IronSource', description: 'IronSource mediation' }
];

// Список стран для ГЕО-ограничений
const COUNTRIES = [
  { code: 'RU', name: 'Россия', flag: '🇷🇺' },
  { code: 'US', name: 'США', flag: '🇺🇸' },
  { code: 'DE', name: 'Германия', flag: '🇩🇪' },
  { code: 'FR', name: 'Франция', flag: '🇫🇷' },
  { code: 'GB', name: 'Великобритания', flag: '🇬🇧' },
  { code: 'CN', name: 'Китай', flag: '🇨🇳' },
  { code: 'JP', name: 'Япония', flag: '🇯🇵' },
  { code: 'KR', name: 'Южная Корея', flag: '🇰🇷' },
  { code: 'IN', name: 'Индия', flag: '🇮🇳' },
  { code: 'BR', name: 'Бразилия', flag: '🇧🇷' },
  { code: 'CA', name: 'Канада', flag: '🇨🇦' },
  { code: 'AU', name: 'Австралия', flag: '🇦🇺' },
  { code: 'IT', name: 'Италия', flag: '🇮🇹' },
  { code: 'ES', name: 'Испания', flag: '🇪🇸' },
  { code: 'NL', name: 'Нидерланды', flag: '🇳🇱' },
  { code: 'SE', name: 'Швеция', flag: '🇸🇪' },
  { code: 'NO', name: 'Норвегия', flag: '🇳🇴' },
  { code: 'FI', name: 'Финляндия', flag: '🇫🇮' },
  { code: 'PL', name: 'Польша', flag: '🇵🇱' },
  { code: 'TR', name: 'Турция', flag: '🇹🇷' }
];

const AdBlockEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewAdBlock = id === 'new';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [applications, setApplications] = useState([]);

  // Форма данных
  const [formData, setFormData] = useState({
    name: '',
    ad_type: 'ads_banners',
    type_sdk: 'yandex',
    code: '',
    sort_order: 0,
    geo_restrictions: { type: 'world', countries: [] },
    applications: [],
    is_active: true,
    settings: {}
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Проверяем что ID валидный
      if (!id) {
        console.error('AdBlockEditPage: ID is undefined');
        setError('Ошибка: ID рекламного блока не определён');
        setLoading(false);
        return;
      }
      
      const isNewAdBlock = id === 'new';
      
      // Загружаем приложения
      const applicationsResponse = await applicationsAPI.getAll();
      setApplications(applicationsResponse.data);

      // Если редактируем существующий блок, загружаем его данные
      if (!isNewAdBlock) {
        const adBlockResponse = await adsAPI.getById(id);
        const adBlock = adBlockResponse.data;
        
        setFormData({
          name: adBlock.name,
          ad_type: adBlock.ad_type,
          type_sdk: adBlock.type_sdk,
          code: adBlock.code,
          sort_order: adBlock.sort_order,
          geo_restrictions: adBlock.geo_restrictions || { type: 'world', countries: [] },
          applications: adBlock.applications || [],
          is_active: adBlock.is_active,
          settings: adBlock.settings || {}
        });
      }
      
      setError('');
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const isNewAdBlock = id === 'new';
      
      if (isNewAdBlock) {
        await adsAPI.create(formData);
      } else {
        await adsAPI.update(id, formData);
      }
      
      navigate('/ads');
    } catch (err) {
      setError('Ошибка сохранения рекламного блока');
      console.error('Error saving ad block:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить этот рекламный блок?')) {
      return;
    }
    
    try {
      await adsAPI.delete(id);
      navigate('/ads');
    } catch (err) {
      setError('Ошибка удаления рекламного блока');
      console.error('Error deleting ad block:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGeoChange = (type, countries = []) => {
    setFormData(prev => ({
      ...prev,
      geo_restrictions: { type, countries }
    }));
  };

  const getAdTypeInfo = (adType) => {
    return AD_TYPES.find(t => t.value === adType) || { label: adType, description: '' };
  };

  const getSdkInfo = (sdk) => {
    return SDK_TYPES.find(s => s.value === sdk) || { label: sdk, description: '' };
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок и кнопки */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/ads')}
          >
            Назад к списку
          </Button>
          <Typography variant="h4" component="h1">
            {isNewAdBlock ? 'Создать рекламный блок' : 'Редактировать рекламный блок'}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isNewAdBlock && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Удалить
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Основная информация */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader title="Основная информация" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Название рекламного блока"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    helperText="Название для удобства управления в админке"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Тип рекламы</InputLabel>
                    <Select
                      value={formData.ad_type}
                      label="Тип рекламы"
                      onChange={(e) => handleInputChange('ad_type', e.target.value)}
                    >
                      {AD_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box>
                            <Typography variant="body1">{type.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {type.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>SDK</InputLabel>
                    <Select
                      value={formData.type_sdk}
                      label="SDK"
                      onChange={(e) => handleInputChange('type_sdk', e.target.value)}
                    >
                      {SDK_TYPES.map((sdk) => (
                        <MenuItem key={sdk.value} value={sdk.value}>
                          <Box>
                            <Typography variant="body1">{sdk.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {sdk.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Код рекламного блока"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                    required
                    helperText="Например: R-M-15935181-3 для Yandex или 1677787 для MyTarget"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Порядок сортировки"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                    helperText="Меньше = выше в списке"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      />
                    }
                    label="Активный рекламный блок"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Настройки доступности */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Настройки доступности" />
            <CardContent>
              {/* ГЕО-ограничения */}
              <Box sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{ mb: 2 }}>
                  ГЕО-ограничения
                </FormLabel>
                <RadioGroup
                  value={formData.geo_restrictions.type}
                  onChange={(e) => handleGeoChange(e.target.value, [])}
                >
                  <FormControlLabel
                    value="world"
                    control={<Radio />}
                    label="Весь мир"
                  />
                  <FormControlLabel
                    value="countries"
                    control={<Radio />}
                    label="Только выбранные страны"
                  />
                </RadioGroup>
                
                {formData.geo_restrictions.type === 'countries' && (
                  <Autocomplete
                    multiple
                    options={COUNTRIES}
                    getOptionLabel={(option) => `${option.flag} ${option.name}`}
                    value={COUNTRIES.filter(country => 
                      formData.geo_restrictions.countries.includes(country.code)
                    )}
                    onChange={(event, newValue) => {
                      const countryCodes = newValue.map(country => country.code);
                      handleGeoChange('countries', countryCodes);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={`${option.flag} ${option.name}`}
                          {...getTagProps({ index })}
                          key={option.code}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Выберите страны"
                        placeholder="Начните вводить название страны"
                        sx={{ mt: 1 }}
                      />
                    )}
                  />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Привязка к приложениям */}
              <Box>
                <FormLabel component="legend" sx={{ mb: 2 }}>
                  Приложения
                </FormLabel>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Если не выбрано ни одного приложения, блок будет доступен для всех приложений
                </Typography>
                
                <Autocomplete
                  multiple
                  options={applications}
                  getOptionLabel={(option) => option.name}
                  value={applications.filter(app => 
                    formData.applications.includes(app.bundle)
                  )}
                  onChange={(event, newValue) => {
                    const bundles = newValue.map(app => app.bundle);
                    handleInputChange('applications', bundles);
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.bundle}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Выберите приложения"
                      placeholder="Начните вводить название приложения"
                    />
                  )}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdBlockEditPage;
